import { FroelingHistoryPoint, PelletUsageBucket, SolarHistoryPoint, TempDataPoint, ThermometerReading, ZisterneHistoryPoint } from '../version.service';
import { LinkWindow } from '../shared/deep-link.service';
import { BoardRef } from './board-catalog';
import { ValveRow, loadValves } from './board-valves';

/**
 * Data of the board's charts for exactly one window [from, to]: /api/history at the chart's resolution
 * (buckets ≈ 2 px per point, at most 2000; minmax=1: a downsampled answer also carries each bucket's
 * min / max, drawn as a band), the KNX valves time-weighted (agg=time), the pellets from
 * /api/pellets/usage. Requests of one window are shared (the Fröling series feeds every heating chart).
 * The stats below the charts come from /api/history/stats: over every raw point of the window (true
 * extremes with their times), so phone, desktop and PDF show the same numbers for the same window. An
 * older backend without it: from one fixed resolution ({@link STAT_BUCKETS}), marked "≈". The Fröling
 * archive holds failed reads (temperatures of exactly 0): the board leaves them out of its points, bands
 * and stats ({@link deglitchFroeling}, {@link withoutReadGlitches}).
 */

export interface XY { x: number; y: number | null; }
/** A KNX weather value; lo / hi: its bucket's min / max (a downsampled answer). */
export interface WxPoint { ts: number; value: number; lo?: number; hi?: number; }

/** One field's summary over every raw point of a window (/api/history/stats). */
export interface FieldStats {
  min: number; minTs: number;
  max: number; maxTs: number;
  /** Arithmetic mean of the points; agg=time: time-weighted over the time known. */
  mean: number;
  /** Points in the window. */
  n: number;
  first: number; firstTs: number;
  last: number; lastTs: number;
  /** agg=time: time shares (sum 1) of the 5 %-wide value bins 0–5, 5–10 … 95–100 %. */
  hist5?: number[];
  /** A Fröling read glitch was left out and the time of an extreme is only its bucket's ({@link withoutReadGlitches}). */
  approx?: boolean;
}

/** /api/history/stats: fields by flattened key ("boilerTempC", "heatingCircuits.Heizkreis 01.flowTempActualC", "value" …). */
export interface HistoryStats {
  series: string;
  from: number;
  to: number;
  points: number;
  fields: Record<string, FieldStats>;
  /** Requested with agg=time. */
  timeWeighted?: boolean;
}

/** A bucket's min / max of the flattened fields (minmax=1 on a downsampled answer). */
export interface BucketMinMax { _min?: Record<string, number>; _max?: Record<string, number>; }

export type BoardData =
  | { kind: 'froeling'; pts: FroelingHistoryPoint[] }
  | { kind: 'solar'; pts: SolarHistoryPoint[] }
  | { kind: 'thermo'; pts: TempDataPoint[]; valve: XY[] | null; valveStepped: boolean;
      /** The KNX series the valve line comes from (agg=time), null: the room points' own valvePct. */
      valveSeries: string | null }
  | { kind: 'pellets'; bars: PelletUsageBucket[]; line: PelletUsageBucket[]; short: boolean }
  | { kind: 'zisterne'; pts: ZisterneHistoryPoint[] }
  | { kind: 'wx'; pts: WxPoint[] }
  | { kind: 'valves'; rows: ValveRow[] };

/** Most points a chart asks for. */
export const MAX_BUCKETS = 2000;
/** Resolution of the fallback stats (an older backend without /api/history/stats): the same on every
 *  screen and in the PDF; up to this many points in a window the archive returns its raw values. */
export const STAT_BUCKETS = MAX_BUCKETS;
/** Pellets: hourly bars (and a 10-min rate line) up to this span, daily bars beyond. */
const PELLET_HOURLY_MAX_MS = 3 * 86_400_000;
/** PWM valves: duty over this window (as the room cards). */
const PWM_WINDOW_MS = 30 * 60_000;
/** A held valve value older than this (or 3 buckets) is a gap. */
export const VALVE_STALE_MS = 30 * 60_000;

/** Buckets for a chart `widthPx` wide: ~2 px per point. */
export function bucketsFor(widthPx: number): number {
  return Math.max(60, Math.min(MAX_BUCKETS, Math.round(widthPx / 2)));
}

/** GET → JSON, shared between the charts of one window (same URL = same request). */
export class BoardLoader {
  private readonly cache = new Map<string, Promise<unknown>>();
  private readonly cleaned = new Map<string, Promise<HistoryStats | null>>();

  get(url: string): Promise<unknown> {
    let p = this.cache.get(url);
    if (!p) {
      p = fetch(url).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); });
      p.catch(() => this.cache.delete(url));   // a failed read is tried again by the next load
      this.cache.set(url, p);
    }
    return p;
  }

  /** minmax: a downsampled answer carries each bucket's _min / _max (an older backend ignores it). */
  history(series: string, win: LinkWindow, buckets: number, agg?: 'time', minmax = false): Promise<any[]> {
    const url = `/api/history?series=${encodeURIComponent(series)}&from=${Math.round(win.from)}&to=${Math.round(win.to)}`
      + `&buckets=${buckets}${agg ? '&agg=' + agg : ''}${minmax ? '&minmax=1' : ''}`;
    return this.get(url).then(v => Array.isArray(v) ? v : []);
  }

  /**
   * The stats of a series over every raw point of the window, null where the backend has none (an older
   * one answers /api/history/stats as /api/history: a list) or the request failed — the caller falls back.
   * The Fröling's without its read glitches ({@link withoutReadGlitches}).
   */
  stats(series: string, win: LinkWindow, agg?: 'time'): Promise<HistoryStats | null> {
    const plain = this.rawStats(series, win, agg);
    if (series !== 'froeling' || agg) return plain;
    const url = statsUrl(series, win);
    let p = this.cleaned.get(url);
    if (!p) {
      p = plain.then(s => s ? withoutReadGlitches(s, win, this).catch(() => s) : null);
      this.cleaned.set(url, p);
    }
    return p;
  }

  /** {@link stats} as the backend answers them. */
  rawStats(series: string, win: LinkWindow, agg?: 'time'): Promise<HistoryStats | null> {
    return this.get(statsUrl(series, win, agg)).then(
      v => isStats(v) ? { ...v, timeWeighted: agg === 'time' } : null,
      () => null);
  }

  pellets(win: LinkWindow, bucket: 'hour' | '10min' | 'day'): Promise<PelletUsageBucket[]> {
    return this.get(`/api/pellets/usage?from=${Math.round(win.from)}&to=${Math.round(win.to)}&bucket=${bucket}`)
      .then(v => Array.isArray(v) ? v as PelletUsageBucket[] : []);
  }
}

/** Live data a chart can fall back on where the archive has none. */
export interface BoardLiveHistory {
  /** The cistern level of the last 7 days (AppComponent's live history). */
  zisterne?: ZisterneHistoryPoint[];
  /** The live KNX readings (the valve overview's rooms and their valve series). */
  rooms?: ThermometerReading[];
}

/** Load the data of one chart for `win`. `room`: the live reading of a KNX room (its valve series). */
export async function loadBoardData(ref: BoardRef, win: LinkWindow, buckets: number, loader: BoardLoader,
                                    room: ThermometerReading | undefined, live: BoardLiveHistory = {}): Promise<BoardData> {
  const byTs = <T extends { ts: number }>(a: T[]) => a.filter(p => typeof p?.ts === 'number').sort((p, q) => p.ts - q.ts);
  switch (ref.kind) {
    case 'froeling':
    case 'hc': {
      const raw = await loader.history('froeling', win, buckets, undefined, true);
      return { kind: 'froeling', pts: deglitchFroeling(raw, bucketSpan(win, buckets)).pts };
    }
    case 'solar':
      return { kind: 'solar', pts: byTs(await loader.history('solar', win, buckets, undefined, true)) };
    case 'zisterne': {
      const archived = byTs(await loader.history('zisterne', win, buckets, undefined, true) as ZisterneHistoryPoint[]);
      // the archive may lag (or miss) the last days: the live 7-day history fills in after its last point
      const last = archived.length ? archived[archived.length - 1].ts : -Infinity;
      const recent = (live.zisterne ?? []).filter(p => typeof p?.ts === 'number' && p.ts > last && p.ts >= win.from && p.ts <= win.to);
      return { kind: 'zisterne', pts: recent.length ? [...archived, ...thin(byTs(recent), buckets, win)] : archived };
    }
    case 'wx': {
      const raw = await loader.history(ref.series, win, buckets, undefined, true);
      const pts: WxPoint[] = [];
      for (const p of raw) {
        const v = typeof p?.value === 'boolean' ? (p.value ? 1 : 0) : p?.value;
        if (typeof p?.ts !== 'number' || typeof v !== 'number' || !isFinite(v)) continue;
        const lo = p._min?.value, hi = p._max?.value;
        pts.push(typeof lo === 'number' && typeof hi === 'number' ? { ts: p.ts, value: v, lo, hi } : { ts: p.ts, value: v });
      }
      return { kind: 'wx', pts: byTs(pts) };
    }
    case 'pellets': {
      const short = win.to - win.from <= PELLET_HOURLY_MAX_MS;
      const [bars, line] = await Promise.all(short
        ? [loader.pellets(win, 'hour'), loader.pellets(win, '10min')]
        : [loader.pellets(win, 'day')]);
      return { kind: 'pellets', bars: byTs(bars), line: byTs(short ? (line ?? []) : bars), short };
    }
    case 'froelingSettings':
      // no chart (board-settings.ts: the live settings snapshot); never loaded
      throw new Error('froeling.settings has no chart data');
    case 'valves':
      return { kind: 'valves', rows: await loadValves(ref.floor, win, buckets, loader, live.rooms ?? []) };
    case 'thermo': {
      const series = room?.valveSeries ?? null;
      // the valve from a little before the window: the position valid at its start (a value holds)
      const valveWin: LinkWindow = { from: win.from - VALVE_STALE_MS, to: win.to };
      const [pts, rawValve] = await Promise.all([
        loader.history('thermo-' + ref.deviceId, win, buckets, undefined, true).then(byTs),
        // agg=time: time-weighted buckets — a PWM valve's long window is its duty, not the mean of 0 / 100 telegrams
        series ? loader.history(series, valveWin, buckets, 'time').catch(() => null) : Promise.resolve(null),
      ]);
      const archive = rawValve ? valveFromArchive(rawValve, room?.valvePwm === true, valveWin, buckets, win) : null;
      if (archive && archive.line.length) return { kind: 'thermo', pts, valve: archive.line, valveStepped: !archive.pwm, valveSeries: series };
      // no archive (none mapped, or not readable): the points' own valve values
      const own = pts.filter(p => typeof p.valvePct === 'number' && isFinite(p.valvePct))
        .map(p => ({ ts: p.ts, pct: clampPct(p.valvePct as number) }));
      const bucketMs = (win.to - win.from) / buckets;
      return { kind: 'thermo', pts, valve: own.length ? holdLine(own, win, Math.max(VALVE_STALE_MS, 3 * bucketMs)) : null, valveStepped: true,
               valveSeries: null };
    }
  }
}

function clampPct(v: number): number { return Math.max(0, Math.min(100, v)); }

/** A /api/history/stats answer (an older backend sends a list). */
function isStats(v: unknown): v is HistoryStats {
  return !!v && typeof v === 'object' && !Array.isArray(v) && typeof (v as any).fields === 'object' && (v as any).fields !== null;
}

function statsUrl(series: string, win: LinkWindow, agg?: 'time'): string {
  return `/api/history/stats?series=${encodeURIComponent(series)}&from=${Math.round(win.from)}&to=${Math.round(win.to)}`
    + (agg ? '&agg=' + agg : '');
}

/** The span of one bucket of `buckets` over `win` (as the backend splits it: the URL's rounded bounds). */
function bucketSpan(win: LinkWindow, buckets: number): number {
  return Math.max(1, Math.round(win.to) - Math.round(win.from)) / buckets;
}

// ── Fröling read glitches ────────────────────────────────────────────────────

/** Fields a failed Fröling read zeroes (flattened keys): its temperatures and the buffer charge. */
export function isGlitchKey(key: string): boolean {
  return /TempC$|(^|\.)flowTempActualC$|^bufferChargePct$/.test(key);
}

/** A read with all of these exactly 0 failed as a whole. */
const FAILED_READ_KEYS = ['boilerTempC', 'bufferTopTempC', 'bufferBottomTempC', 'hotWaterTempC'];
/** An exact 0 between two values above this is a glitch: nothing here drops 5 K (%) and back within a poll. */
const GLITCH_NEIGHBOUR = 5;
/** The Fröling service polls once a minute (the points in a bucket of a downsampled answer). */
const FROELING_POLL_MS = 60_000;

/** The Fröling points without read glitches ({@link deglitchFroeling}). */
export interface Deglitched {
  /** By ts; changed points are copies. */
  pts: any[];
  /** Field → zeros left out (a downsampled answer: its buckets with one — at least that many). */
  glitches: Map<string, number>;
  /** Field → ts of the points / buckets whose value (and minimum) is now an estimate. */
  estimated: Map<string, Set<number>>;
  /** Downsampled: buckets with _min / _max. */
  bucketed: boolean;
}

/** A flattened field of a point: "boilerTempC", "heatingCircuits.Heizkreis 01.flowTempActualC". */
export function flatValue(p: any, key: string): number | null {
  const a = key.indexOf('.');
  if (a < 0) return finite(p?.[key]);
  const b = key.lastIndexOf('.');
  const arr = p?.[key.slice(0, a)];
  const name = key.slice(a + 1, b);
  return finite(Array.isArray(arr) ? arr.find((e: any) => e?.name === name)?.[key.slice(b + 1)] : undefined);
}

function finite(v: unknown): number | null {
  return typeof v === 'number' && isFinite(v) ? v : null;
}

/** New values of one point: fields (flattened keys), bucket minima and maxima. */
interface Patch { vals: Map<string, number>; min: Map<string, number>; max: Map<string, number>; }

function patched(p: any, q: Patch): any {
  const out: any = { ...p };
  for (const [k, v] of q.vals) {
    const a = k.indexOf('.');
    if (a < 0) { out[k] = v; continue; }
    const b = k.lastIndexOf('.'), arr = k.slice(0, a), name = k.slice(a + 1, b), f = k.slice(b + 1);
    if (Array.isArray(out[arr])) out[arr] = out[arr].map((e: any) => e?.name === name ? { ...e, [f]: v } : e);
  }
  if (q.min.size) out._min = { ...out._min, ...Object.fromEntries(q.min) };
  if (q.max.size) out._max = { ...out._max, ...Object.fromEntries(q.max) };
  return out;
}

/** The glitch-prone fields of the points (flattened keys). */
function glitchKeys(pts: readonly any[], bucketed: boolean): string[] {
  const keys = new Set<string>();
  for (const p of pts) {
    if (bucketed) {
      for (const k of Object.keys(p?._min ?? {})) if (isGlitchKey(k)) keys.add(k);
      continue;
    }
    for (const [k, v] of Object.entries(p ?? {})) {
      if (typeof v === 'number') { if (isGlitchKey(k)) keys.add(k); continue; }
      if (!Array.isArray(v)) continue;
      for (const e of v) {
        if (!e || typeof e.name !== 'string') continue;
        for (const [f, fv] of Object.entries(e)) if (typeof fv === 'number' && isGlitchKey(`${k}.${e.name}.${f}`)) keys.add(`${k}.${e.name}.${f}`);
      }
    }
  }
  return [...keys];
}

const deglitchMemo = new WeakMap<object, Deglitched>();

/**
 * The Fröling points (an /api/history answer, `bucketMs` its bucket span) without the archive's failed reads.
 * Raw points: a failed read (FAILED_READ_KEYS all 0) is left out; an isolated 0 of a field (its values before
 * and after above GLITCH_NEIGHBOUR) becomes the value between its neighbours. Buckets (minmax=1): one holding
 * nothing but failed reads is left out; where a field's minimum is such a 0 — isolated, or in a bucket holding
 * a failed read — its mean is taken as if one poll of the bucket were missing (at least the value between its
 * neighbours, at most its maximum) and its minimum as not above that mean, between the neighbours' minima (a
 * bucket of nothing but the 0: the value between its neighbours). Neighbours: the nearest without a glitch.
 * `raw` stays unchanged (the loader shares it); memoised per answer.
 */
export function deglitchFroeling(raw: readonly any[], bucketMs: number): Deglitched {
  const hit = deglitchMemo.get(raw);
  if (hit) return hit;
  const pts = raw.filter(p => typeof p?.ts === 'number').sort((p, q) => p.ts - q.ts);
  const bucketed = pts.some(p => p._min && typeof p._min === 'object');
  const lo = (p: any, k: string) => bucketed ? finite(p._min?.[k]) : flatValue(p, k);
  const hi = (p: any, k: string) => bucketed ? finite(p._max?.[k]) : flatValue(p, k);
  // holds a failed read (buckets: their minima) / nothing else (buckets: their maxima too)
  const holdsFailed = (p: any) => FAILED_READ_KEYS.every(k => lo(p, k) === 0);
  const onlyFailed = (p: any) => FAILED_READ_KEYS.every(k => hi(p, k) === 0);
  const keys = glitchKeys(pts, bucketed);
  const glitches = new Map<string, number>();
  const estimated = new Map<string, Set<number>>();
  const count = (k: string) => glitches.set(k, (glitches.get(k) ?? 0) + 1);
  const kept: any[] = [];
  for (const p of pts) {
    if (!onlyFailed(p)) { kept.push(p); continue; }
    for (const k of keys) if (lo(p, k) === 0) count(k);
  }
  const patches = new Map<number, Patch>();
  const polls = Math.max(2, bucketMs / FROELING_POLL_MS);
  for (const k of keys) {
    const idx: number[] = [];
    for (let i = 0; i < kept.length; i++) if (lo(kept[i], k) != null) idx.push(i);
    // a neighbour above GLITCH_NEIGHBOUR: its minimum — or, a bucket with a glitch of its own, its mean
    const above = (n: any) => lo(n, k)! > GLITCH_NEIGHBOUR || (bucketed && lo(n, k) === 0 && (flatValue(n, k) ?? 0) > GLITCH_NEIGHBOUR);
    const glitch = idx.map((i, j) => {
      const p = kept[i];
      if (lo(p, k) !== 0) return false;
      const prev = j > 0 ? kept[idx[j - 1]] : null, next = j + 1 < idx.length ? kept[idx[j + 1]] : null;
      if ((prev || next) && (!prev || above(prev)) && (!next || above(next))) return true;
      return bucketed && (hi(p, k) ?? 0) > 0 && holdsFailed(p);
    });
    for (let j = 0; j < idx.length; j++) {
      if (!glitch[j]) continue;
      const p = kept[idx[j]];
      // estimates from the nearest neighbours without a glitch: the value at p.ts on the line between theirs
      let ja = j - 1, jb = j + 1;
      while (ja >= 0 && glitch[ja]) ja--;
      while (jb < idx.length && glitch[jb]) jb++;
      const prev = ja >= 0 ? kept[idx[ja]] : null, next = jb < idx.length ? kept[idx[jb]] : null;
      const between = (at: (n: any) => number | null) => {
        const va = prev && at(prev), vb = next && at(next);
        return va != null && vb != null ? va + (vb - va) * (p.ts - prev.ts) / Math.max(1, next.ts - prev.ts) : va ?? vb;
      };
      const line = between(n => flatValue(n, k));
      const top = hi(p, k) ?? 0, mean = flatValue(p, k);
      const q: Patch = patches.get(idx[j]) ?? { vals: new Map(), min: new Map(), max: new Map() };
      if (!bucketed || top <= 0 || mean == null) {
        // a raw point / a bucket of nothing but the 0
        if (line == null) continue;
        q.vals.set(k, line);
        if (bucketed) { q.min.set(k, line); q.max.set(k, line); }
      } else {
        // the mean as if one poll were missing (more zeros: at least the neighbours' line), not above the maximum;
        // the minimum not above it, on the line between the neighbours' minima
        const m = Math.min(top, Math.max(mean * polls / (polls - 1), line ?? -Infinity));
        q.vals.set(k, m);
        q.min.set(k, Math.min(m, between(n => lo(n, k)) ?? m));
      }
      patches.set(idx[j], q);
      count(k);
      let at = estimated.get(k);
      if (!at) estimated.set(k, at = new Set());
      at.add(p.ts);
    }
  }
  const r: Deglitched = { pts: patches.size ? kept.map((p, i) => patches.has(i) ? patched(p, patches.get(i)!) : p) : kept,
                          glitches, estimated, bucketed };
  deglitchMemo.set(raw, r);
  return r;
}

/**
 * The Fröling stats without its read glitches: where a field's min (max) is an exact 0 and the window's points at
 * the stats resolution ({@link STAT_BUCKETS}, {@link deglitchFroeling}) leave zeros of it out, the extreme of the
 * values left (a real 0: with its own time), the mean and the count without the zeros. A downsampled answer's
 * extreme is its bucket's: that bucket's own stats give the exact value and time (failing that, the bucket's
 * time, "≈"). A backend that drops them itself has no such 0: unchanged.
 */
export async function withoutReadGlitches(hs: HistoryStats, win: LinkWindow, loader: BoardLoader): Promise<HistoryStats> {
  const suspect = Object.entries(hs.fields).filter(([k, f]) => isGlitchKey(k) && f?.n > 0 && (f.min === 0 || f.max === 0));
  if (!suspect.length) return hs;
  const bucketMs = bucketSpan(win, STAT_BUCKETS);
  const dg = deglitchFroeling(await loader.history(hs.series, win, STAT_BUCKETS, undefined, true), bucketMs);
  const from = Math.round(win.from), to = Math.round(win.to);
  // the exact time of `v` (the min / max of field k) in the bucket at `ts`, null: not found
  const exactTs = async (k: string, which: 'min' | 'max', v: number, ts: number): Promise<number | null> => {
    const i = Math.floor((ts - from) / bucketMs);
    const sub = await loader.rawStats(hs.series, { from: Math.ceil(from + i * bucketMs), to: Math.ceil(from + (i + 1) * bucketMs) - 1 });
    const f = sub?.fields?.[k];
    return f && f[which] === v ? (which === 'min' ? f.minTs : f.maxTs) : null;
  };
  const fields = { ...hs.fields };
  await Promise.all(suspect.map(async ([k, f]) => {
    const zeros = dg.glitches.get(k) ?? 0;
    if (!zeros) return;
    const out: FieldStats = { ...f };
    if (f.n > zeros) { out.mean = f.mean * f.n / (f.n - zeros); out.n = f.n - zeros; }
    // the extremes of the values left (not the estimates; ties: the earliest)
    const est = dg.estimated.get(k);
    let min: number | null = null, minTs = 0, max: number | null = null, maxTs = 0;
    for (const p of dg.pts) {
      if (p.ts < from || p.ts > to || est?.has(p.ts)) continue;
      const a = dg.bucketed ? finite(p._min?.[k]) : flatValue(p, k);
      const b = dg.bucketed ? finite(p._max?.[k]) : a;
      if (a != null && (min == null || a < min)) { min = a; minTs = p.ts; }
      if (b != null && (max == null || b > max)) { max = b; maxTs = p.ts; }
    }
    const fix = async (which: 'min' | 'max', v: number, ts: number) => {
      const exact = dg.bucketed ? await exactTs(k, which, v, ts) : ts;
      if (which === 'min') { out.min = v; out.minTs = exact ?? ts; } else { out.max = v; out.maxTs = exact ?? ts; }
      if (exact == null) out.approx = true;
    };
    // the endpoint's 0 is the earliest; one of the values left (a real 0 too) has its own time
    const slot = (ts: number) => dg.bucketed ? Math.floor((ts - from) / bucketMs) : ts;
    if (f.min === 0 && min != null && min >= 0 && slot(minTs) !== slot(f.minTs)) await fix('min', min, minTs);
    if (f.max === 0 && max != null && max <= 0 && slot(maxTs) !== slot(f.maxTs)) await fix('max', max, maxTs);
    fields[k] = out;
  }));
  return { ...hs, fields };
}

/** A stats source: a series, time-weighted or not. */
export interface StatSource { series: string; agg?: 'time'; }

/** Key of a stats source in {@link ExactStats}. */
export function statSourceKey(s: StatSource): string { return `${s.series}|${s.agg ?? ''}`; }

/** The stats of a chart's sources by {@link statSourceKey}: null = the backend has none (fall back). */
export type ExactStats = Map<string, HistoryStats | null>;

/** The stats sources a chart of `ref` will use, known before its data (fetched along with it). */
export function statSourcesOf(ref: BoardRef, room: ThermometerReading | undefined): StatSource[] {
  switch (ref.kind) {
    case 'froeling':
    case 'hc': return [{ series: 'froeling' }];
    case 'solar': return [{ series: 'solar' }];
    case 'zisterne': return [{ series: 'zisterne' }];
    case 'wx': return [{ series: ref.series, agg: 'time' }];
    case 'thermo': {
      const out: StatSource[] = [{ series: 'thermo-' + ref.deviceId }];
      if (room?.valveSeries) out.push({ series: room.valveSeries, agg: 'time' });
      return out;
    }
    default: return [];
  }
}

/** Fetch the stats of `sources` over `win` (missing ones only, into `into`). */
export async function loadExactStats(sources: StatSource[], win: LinkWindow, loader: BoardLoader,
                                     into: ExactStats = new Map()): Promise<ExactStats> {
  const todo = new Map<string, StatSource>();
  for (const s of sources) { const k = statSourceKey(s); if (!into.has(k)) todo.set(k, s); }
  const got = await Promise.all([...todo].map(([k, s]) => loader.stats(s.series, win, s.agg).then(r => [k, r] as const)));
  for (const [k, r] of got) into.set(k, r);
  return into;
}

/**
 * Valve archive ({ts, value}, requested for `req` = the window and a little before) → chart points: a held
 * position (see {@link holdLine}); a 2-point / PWM output (the reading says so, or the series only switches
 * 0 ↔ 100 %) as its duty over the last 30 min on a regular grid, like the room cards.
 */
export function valveFromArchive(raw: any[], pwmHint: boolean, req: LinkWindow, buckets: number, win: LinkWindow): { line: XY[]; pwm: boolean } {
  const pts: { ts: number; pct: number }[] = [];
  for (const p of raw) {
    const v = typeof p?.value === 'boolean' ? (p.value ? 100 : 0) : p?.value;
    if (typeof p?.ts === 'number' && typeof v === 'number' && isFinite(v)) pts.push({ ts: p.ts, pct: clampPct(v) });
  }
  pts.sort((a, b) => a.ts - b.ts);
  let flips = 0, binary = true;
  for (let i = 0; i < pts.length && binary; i++) {
    binary = pts[i].pct <= 0.5 || pts[i].pct >= 99.5;
    if (i && (pts[i].pct >= 50) !== (pts[i - 1].pct >= 50)) flips++;
  }
  const pwm = pwmHint || (binary && flips >= 4);
  const bucketMs = (req.to - req.from) / buckets;
  const stale = Math.max(VALVE_STALE_MS, 3 * bucketMs);
  const line: XY[] = [];
  if (!pts.length) return { line, pwm };
  if (!pwm) {
    // more telegrams than buckets: the archive sent time-weighted bucket means (at the buckets' centres),
    // each the position over its own bucket
    const bucketed = pts.every(p => {
      const k = (p.ts - req.from) / bucketMs - 0.5;
      return Math.abs(k - Math.round(k)) * bucketMs < 2;
    });
    const held = bucketed ? pts.map(p => ({ ts: p.ts - bucketMs / 2, pct: p.pct })) : pts;
    return { line: holdLine(held, win, bucketed ? bucketMs + 2 : stale), pwm };
  }
  // duty on a grid of max(bucket, 5 min) steps
  const step = Math.max(bucketMs, 5 * 60_000);
  let j = 0;
  for (let ts = Math.max(win.from, pts[0].ts); ts <= win.to; ts += step) {
    while (j + 1 < pts.length && pts[j + 1].ts <= ts) j++;
    if (pts[j].ts > ts || ts - pts[j].ts > stale) { line.push({ x: ts, y: null }); continue; }
    line.push({ x: ts, y: dutyAt(pts, j, ts) });
  }
  return { line, pwm };
}

/**
 * Sample-and-hold points for a stepped line (Chart.js stepped 'before') and its time-weighted stats: each
 * value holds from its time until the next one, at most `hold` (then a gap: an end point and a null), never
 * beyond the window's end or now. A value from before the window that still holds starts at its start.
 */
function holdLine(pts: { ts: number; pct: number }[], win: LinkWindow, hold: number): XY[] {
  const end = Math.min(win.to, Date.now());
  const line: XY[] = [];
  for (let i = 0; i < pts.length; i++) {
    const start = pts[i].ts;
    if (start >= end) break;
    const next = i + 1 < pts.length ? pts[i + 1].ts : Infinity;
    const until = Math.min(next, start + hold, end);
    if (until <= win.from) continue;
    line.push({ x: Math.max(start, win.from), y: pts[i].pct });
    if (until < next) {
      line.push({ x: until, y: pts[i].pct });   // the held value up to here …
      if (until < end) line.push({ x: until, y: null });   // … then nothing known
    }
  }
  return line;
}

/** At most about `buckets` points of a live history over the window (every n-th; the last one kept). */
function thin<T extends { ts: number }>(pts: T[], buckets: number, win: LinkWindow): T[] {
  const n = Math.ceil(pts.length / Math.max(1, buckets * (pts[pts.length - 1].ts - pts[0].ts + 1) / Math.max(1, win.to - win.from)));
  if (n <= 1) return pts;
  return pts.filter((_, i) => i % n === 0 || i === pts.length - 1);
}

/** Time-weighted mean of the held values over the 30 min before `ts` (`last`: the last point ≤ ts). */
function dutyAt(pts: { ts: number; pct: number }[], last: number, ts: number): number {
  const from = ts - PWM_WINDOW_MS;
  let i = last;
  while (i > 0 && pts[i].ts > from) i--;
  let sum = 0, covered = 0;
  for (; i <= last; i++) {
    const a = Math.max(from, pts[i].ts);
    const b = i < last ? Math.min(ts, pts[i + 1].ts) : ts;
    if (b <= a) continue;
    sum += pts[i].pct * (b - a);
    covered += b - a;
  }
  return covered > 0 ? sum / covered : pts[last].pct;
}
